/**
 * Product Spec Tabs — Concept-safe, interaction pass V6.
 * Scoped to [data-spec-tabs]; does not touch Concept drawer/modal systems.
 */
(function () {
  const reduced = () => window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;

  function safeHttpUrl(raw) {
    const trimmed = String(raw || '').trim();
    if (!trimmed) return null;
    try {
      const url = new URL(trimmed, window.location.href);
      return /^https?:$/i.test(url.protocol) ? url : null;
    } catch {
      return null;
    }
  }

  function parseStartSeconds(value) {
    const raw = String(value || '').trim();
    if (!raw) return 0;
    if (/^\d+$/.test(raw)) return Number(raw) || 0;
    if (/^\d+:\d+(?::\d+)?$/.test(raw)) {
      return raw.split(':').map(Number).reduce((total, part) => total * 60 + part, 0) || 0;
    }
    let total = 0;
    const match = raw.match(/(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?/i);
    if (match) total = (Number(match[1]) || 0) * 3600 + (Number(match[2]) || 0) * 60 + (Number(match[3]) || 0);
    return total;
  }

  function youtubeEmbed(raw, idHint = '') {
    let id = String(idHint || '').trim();
    let start = 0;
    const url = safeHttpUrl(raw);

    if (url) {
      const host = url.hostname.replace(/^www\./i, '').toLowerCase();
      if (host === 'youtu.be') {
        id = id || url.pathname.split('/').filter(Boolean)[0] || '';
      } else if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'music.youtube.com' || host === 'youtube-nocookie.com') {
        const parts = url.pathname.split('/').filter(Boolean);
        if (url.pathname.startsWith('/watch')) id = id || url.searchParams.get('v') || '';
        else if (['embed', 'shorts', 'live', 'v'].includes(parts[0])) id = id || parts[1] || '';
      }
      start = parseStartSeconds(url.searchParams.get('start') || url.searchParams.get('t'));
    }

    if (!/^[a-zA-Z0-9_-]{11}$/.test(id)) return '';
    const params = new URLSearchParams({ autoplay: '1', playsinline: '1', rel: '0', modestbranding: '1' });
    if (start > 0) params.set('start', String(start));
    return `https://www.youtube.com/embed/${encodeURIComponent(id)}?${params.toString()}`;
  }

  function vimeoEmbed(raw, idHint = '') {
    let id = String(idHint || '').trim();
    const url = safeHttpUrl(raw);
    if (url) {
      const host = url.hostname.replace(/^www\./i, '').toLowerCase();
      if (host === 'vimeo.com' || host === 'player.vimeo.com') {
        const parts = url.pathname.split('/').filter(Boolean);
        const candidate = parts.reverse().find((part) => /^\d+$/.test(part));
        id = id || candidate || '';
      }
    }
    if (!/^\d+$/.test(id)) return '';
    return `https://player.vimeo.com/video/${encodeURIComponent(id)}?autoplay=1&title=0&byline=0&portrait=0`;
  }

  function directVideoUrl(raw) {
    const url = safeHttpUrl(raw);
    if (!url) return '';
    const path = url.pathname.toLowerCase();
    const host = url.hostname.toLowerCase();
    if (/\.(mp4|webm|m4v|mov)(?:$|\?)/i.test(`${path}${url.search}`) || host.includes('cdn.shopify.com') && path.includes('/videos/')) {
      return url.href;
    }
    return '';
  }

  function buildPlayableSource(wrap) {
    const raw = wrap.dataset.specVideoUrl || '';
    const id = wrap.dataset.specVideoId || '';
    const hostHint = String(wrap.dataset.specVideoHost || '').toLowerCase();

    if (hostHint.includes('vimeo')) {
      const src = vimeoEmbed(raw, id);
      if (src) return { type: 'iframe', src, title: 'Vimeo video' };
    }
    if (hostHint.includes('youtube') || hostHint.includes('youtu')) {
      const src = youtubeEmbed(raw, id);
      if (src) return { type: 'iframe', src, title: 'YouTube video' };
    }

    const youtube = youtubeEmbed(raw, id);
    if (youtube) return { type: 'iframe', src: youtube, title: 'YouTube video' };

    const vimeo = vimeoEmbed(raw, id);
    if (vimeo) return { type: 'iframe', src: vimeo, title: 'Vimeo video' };

    const direct = directVideoUrl(raw);
    if (direct) return { type: 'video', src: direct, title: 'Product video' };

    return null;
  }

  function resetSpecTabVideos(panel) {
    if (!panel) return;
    panel.querySelectorAll('[data-spec-video-frame]').forEach((frame) => {
      frame.hidden = true;
      frame.replaceChildren();
    });
    panel.querySelectorAll('[data-spec-video]').forEach((wrap) => wrap.classList.remove('is-playing'));
    panel.querySelectorAll('.hk-spec-tabs__video-poster').forEach((el) => { el.hidden = false; });
    panel.querySelectorAll('[data-spec-video-open]').forEach((btn) => {
      btn.hidden = false;
      btn.disabled = false;
      btn.removeAttribute('data-video-error');
    });
  }

  function initSpecTabVideos(root) {
    root.querySelectorAll('[data-spec-video]').forEach((wrap) => {
      if (wrap.dataset.specVideoBound === 'true') return;
      const btn = wrap.querySelector('[data-spec-video-open]');
      const poster = wrap.querySelector('.hk-spec-tabs__video-poster');
      const frame = wrap.querySelector('[data-spec-video-frame]');
      const nativeTemplate = wrap.querySelector('[data-spec-video-native-template]');
      if (!btn || !frame) return;

      const openVideo = () => {
        frame.replaceChildren();
        let playable = null;

        if (nativeTemplate?.content) {
          frame.append(nativeTemplate.content.cloneNode(true));
          const video = frame.querySelector('video');
          if (video) {
            video.controls = true;
            video.playsInline = true;
            video.preload = 'metadata';
            playable = { type: 'native', node: video };
          }
        } else {
          const source = buildPlayableSource(wrap);
          if (source?.type === 'iframe') {
            const iframe = document.createElement('iframe');
            iframe.src = source.src;
            iframe.title = source.title;
            iframe.loading = 'eager';
            iframe.setAttribute('allowfullscreen', '');
            iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share');
            frame.append(iframe);
            playable = { type: 'iframe', node: iframe };
          } else if (source?.type === 'video') {
            const video = document.createElement('video');
            video.src = source.src;
            video.controls = true;
            video.autoplay = true;
            video.playsInline = true;
            video.preload = 'metadata';
            frame.append(video);
            playable = { type: 'native', node: video };
          }
        }

        if (!playable) {
          btn.dataset.videoError = 'true';
          btn.setAttribute('aria-label', 'Video source unavailable');
          return;
        }

        frame.hidden = false;
        btn.hidden = true;
        if (poster) poster.hidden = true;
        wrap.classList.add('is-playing');

        if (playable.type === 'native') {
          const promise = playable.node.play?.();
          if (promise?.catch) promise.catch(() => {});
        }
      };

      btn.addEventListener('click', openVideo);
      wrap.dataset.specVideoBound = 'true';
    });
  }

  function initSpecTabs(root) {
    if (!root || root.dataset.specTabsReady === 'true') return;
    const buttons = Array.from(root.querySelectorAll('[data-spec-tab-btn]'));
    const panels = Array.from(root.querySelectorAll('[data-spec-tab-panel]'));
    if (!buttons.length || !panels.length) return;
    initSpecTabVideos(root);

    const tabList = root.querySelector('.hk-spec-tabs__tab-list');

    const centerActiveTab = (btn) => {
      if (!btn || !tabList) return;
      const left = btn.offsetLeft - (tabList.clientWidth - btn.offsetWidth) / 2;
      tabList.scrollTo({
        left: Math.max(0, left),
        behavior: reduced() ? 'auto' : 'smooth'
      });
    };

    const activate = (index, focus=false) => {
      const target=String(index);
      let activeButton = null;
      buttons.forEach((btn) => {
        const active = btn.dataset.tabIndex === target;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-selected', active ? 'true' : 'false');
        btn.tabIndex = active ? 0 : -1;
        if(active){ activeButton = btn; if(focus) btn.focus({ preventScroll: true }); }
      });
      if (activeButton) centerActiveTab(activeButton);
      panels.forEach((panel) => {
        const active = panel.dataset.tabIndex === target;
        if (active) {
          panel.removeAttribute('hidden');
          panel.classList.remove('is-active','is-entering');
          requestAnimationFrame(()=>requestAnimationFrame(()=>panel.classList.add('is-active','is-entering')));
          setTimeout(()=>panel.classList.remove('is-entering'),500);
        } else {
          panel.classList.remove('is-active','is-entering');
          panel.setAttribute('hidden', '');
          resetSpecTabVideos(panel);
        }
      });
    };

    buttons.forEach((btn,i) => {
      btn.addEventListener('click',(e)=>{
        e.preventDefault();
        activate(btn.dataset.tabIndex);
      });
      btn.addEventListener('keydown',(e)=>{
        let next=null;
        if(e.key==='ArrowRight') next=(i+1)%buttons.length;
        if(e.key==='ArrowLeft') next=(i-1+buttons.length)%buttons.length;
        if(e.key==='Home') next=0;
        if(e.key==='End') next=buttons.length-1;
        if(next!==null){e.preventDefault();activate(buttons[next].dataset.tabIndex,true);}
      });
    });

    let resizeTimer = null;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const active = buttons.find((btn) => btn.classList.contains('is-active'));
        if (active) centerActiveTab(active);
      }, 120);
    }, { passive: true });

    const panel=root.querySelector('.hk-spec-tabs__panel.is-active');
    if(panel) panel.classList.add('is-entering');
    root.dataset.specTabsReady = 'true';
  }

  function initAllSpecTabs(scope=document) {
    scope.querySelectorAll('[data-spec-tabs]').forEach(initSpecTabs);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',()=>initAllSpecTabs());
  else initAllSpecTabs();
  document.addEventListener('shopify:section:load',(e)=>initAllSpecTabs(e.target));
})();
